"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeaturesClient = void 0;
const store_1 = require("./store");
const converters_1 = require("./converters");
/**
 * Represents the FeaturesClient implementation for interacting with
 * core-features assignments.
 *
 * @property featuresStore the Store used to fetch assignments.
 * @property serviceClientCache service used to store the assignments in the cache.
 * @property assignmentListener Optional. A listener for each assignment returned.
 */
class FeaturesClient {
    featuresStore;
    serviceClientCache;
    assignmentListener;
    targetedRuntime;
    hydrateTargetingProperties;
    /**
     * Builds a new FeaturesClient given an HttpClient and an optional list
     * of Features to automatically get assignments for.
     *
     * @param httpClient The mandatory HttpClient the client will use to fetch data.
     * @param serviceCache service in charge of cache logic implementation.
     * @param assignmentListener Optional. A listener for each assignment returned.
     * @param targetedRuntime Optional. A default set of runtime targeting properties to auto add on all evaluations.
     * @param evaluationCriterias Optional. The optional list of features to automatically fetch assignments for.
     * @param hydrateTargetingProperties Optional. A flag to hydrate the targeting properties on the edge-core-api level.
     */
    constructor(httpClient, serviceCache, assignmentListener, targetedRuntime, evaluationCriterias, hydrateTargetingProperties) {
        this.featuresStore = new store_1.FeaturesStore(httpClient);
        this.serviceClientCache = serviceCache;
        this.assignmentListener = assignmentListener;
        this.targetedRuntime = targetedRuntime;
        this.hydrateTargetingProperties = hydrateTargetingProperties ?? false;
        void this.init(evaluationCriterias);
    }
    /**
     * Inits the object configuration, if a list of EvaluationCriteria is sent,
     * will fetch the assignments and store it in the cache.
     *
     * @param evaluationCriterias array of EvaluationCriteria (optional)
     */
    async init(evaluationCriterias) {
        await this.fetchAssignments(evaluationCriterias);
    }
    /**
     * Retrieves a list of FeatureAssignments from the API and inserts them into the cache.
     * Each FeatureAssignment is associated with an EvaluationCriteria object, using the shared
     * 'featureKey' as the link between them. The EvaluationCriteria serves as the key in the cache.
     *
     * @param evaluationCriterias array of EvaluationCriteria that will be evaluated using the API
     * and stored in the cache.
     */
    async fetchAssignments(evaluationCriterias) {
        if (!evaluationCriterias || evaluationCriterias.length == 0) {
            return;
        }
        // set default target runtime properties
        const enrichedCriteria = evaluationCriterias.map((evaluationCriteria) => this.addDefaultTargetedRuntime(evaluationCriteria));
        const evaluatedFeatures = await this.retrieveFeatureAssignmentsFromStore(enrichedCriteria);
        if (evaluatedFeatures && Array.isArray(evaluatedFeatures)) {
            this.saveAll(enrichedCriteria, evaluatedFeatures);
        }
        else {
            console.warn('Error retrieving features.');
        }
    }
    /**
     * Creates a new TargetingProperties object with default target runtime properties
     * if the given evaluationCriteria does not have them defined.
     *
     * @param evaluationCriteria
     * @returns a new runtime properties enriched evaluationCriteria
     */
    addDefaultTargetedRuntime(evaluationCriteria) {
        if (!this.targetedRuntime)
            return evaluationCriteria;
        if (evaluationCriteria.targetingProperties?.runtime)
            return evaluationCriteria;
        return {
            ...evaluationCriteria,
            targetingProperties: {
                ...evaluationCriteria.targetingProperties,
                runtime: { ...this.targetedRuntime },
            },
        };
    }
    /**
     * Retrieves a FeatureAssignment based on given evaluation criteria.
     *
     * This method attempts to find a FeatureAssignment by first searching in the local cache.
     * If the assignment is not found in the cache, it makes a call to the feature store to
     * retrieve it. Once retrieved from the feature store, the assignment is stored in the local
     * cache for future reference.
     *
     * The method operates on the principle of cache-first retrieval to optimize performance
     * and reduce unnecessary calls to the feature store.
     *
     * @param evaluationCriteria The EvaluationCriteria object used to identify the corresponding
     *                           FeatureAssignment.
     * @returns {FeatureAssignment} Returns the FeatureAssignment if found either in the cache
     *                              or the feature store; Returns a null FeatureAssignment if there is
     *                              an error.
     */
    async getAssignment(evaluationCriteria) {
        const enrichedCriteria = this.addDefaultTargetedRuntime(evaluationCriteria);
        const featureAssignment = await this.getSingleAssignment(enrichedCriteria);
        this.notifyAssignmentListener(featureAssignment);
        return featureAssignment;
    }
    /**
     * Retrieves a FeatureAssignment based on given evaluation criteria.
     *
     * This method attempts to find a FeatureAssignment by first searching in the local cache.
     * If the assignment is not found in the cache, it makes a call to the feature store to
     * retrieve it. Once retrieved from the feature store, the assignment is stored in the local
     * cache for future reference.
     *
     * @param evaluationCriteria The EvaluationCriteria object used to identify the corresponding
     *                           FeatureAssignment.
     * @returns {FeatureAssignment} Returns the FeatureAssignment if found either in the cache
     *                              or the feature store; Returns a null FeatureAssignment if there is
     *                              an error.
     */
    async getSingleAssignment(evaluationCriteria) {
        return (this.serviceClientCache.get(evaluationCriteria) ||
            (await this.fetchAndCacheAssignment(evaluationCriteria)));
    }
    /**
     * Calls featureStore to get an assignment and store it into the cache.
     *
     * @param evaluationCriteria
     * @returns featureAssigment | null
     */
    async fetchAndCacheAssignment(evaluationCriteria) {
        const featureAssignments = await this.retrieveFeatureAssignmentsFromStore([
            evaluationCriteria,
        ]);
        const featureAssignment = featureAssignments?.[0];
        this.save(evaluationCriteria, featureAssignment);
        return featureAssignment;
    }
    /**
     * Asynchronously retrieves and pre-processes feature assignments based on the provided evaluation criteria.
     * This method fetches feature assignments from the features store using the given array of evaluation criteria.
     * Each retrieved feature assignment is then processed by setting its 'value' property, which is determined
     * by the `getFeatureAssignmentValue` function.
     *
     * @param {EvaluationCriteria[]} evaluationCriterias - An array of evaluation criteria used to
     * fetch feature assignments.
     * @returns {Promise<FeatureAssignment[]>} A promise that resolves to an array of feature assignments
     * with updated values.
     */
    async retrieveFeatureAssignmentsFromStore(evaluationCriterias) {
        return (await this.featuresStore.fetchAssignments(evaluationCriterias, this.hydrateTargetingProperties))?.map((feature) => ({
            ...feature,
            value: (0, converters_1.getFeatureAssignmentValue)(feature),
        }));
    }
    /**
     * Caches the EvaluationCriteria and FeatureAssignment based on a matching featureKey.
     * @see this.saveAll for more information.
     * @param evaluationCriteria A single EvaluationCriteria object.
     * @param featureAssignment A single FeatureAssignment object.
     */
    save(evaluationCriteria, featureAssignment) {
        this.saveAll([evaluationCriteria], [featureAssignment]);
    }
    /**
     * Caches pairs of EvaluationCriteria and FeatureAssignment based on a matching featureKey.
     *
     * This method iterates through the provided arrays of EvaluationCriteria and FeatureAssignment,
     * identifying pairs that share the same featureKey. Each identified pair is then saved into the
     * cache if the assignmentKey is defined, that includes also empty value returned by core-features
     * when the FeatureAssignment wasn't found.
     * The purpose is to synchronize and associate evaluation
     * criteria with their corresponding feature assignments as received from the API.
     *
     * @param evaluationCriterias Array of EvaluationCriteria objects. These are the criteria used for evaluation.
     * @param featureAssignments Array of FeatureAssignment objects. These are the results from the API
     * after applying the evaluation criteria.
     */
    saveAll(evaluationCriterias, featureAssignments) {
        const featureAssignmentMap = new Map();
        evaluationCriterias.forEach((feature) => featureAssignmentMap.set(feature.featureKey, feature));
        featureAssignments.forEach((featureAssignment) => {
            const criteria = featureAssignmentMap.get(featureAssignment.featureKey);
            const storeAssignmentInCache = criteria &&
                featureAssignment.assignmentKey !== undefined &&
                !featureAssignment.errorValue;
            if (storeAssignmentInCache) {
                this.serviceClientCache.put(criteria, featureAssignment);
            }
        });
    }
    /**
     * Invoke the [assignmentListener] with the given FeatureAssignment.
     * If no [assignmentListener] is defined, this method no-ops.
     * If the [assignmentListener] throws an error, the error is logged and the
     * exception is suppressed.
     * If the [assignment] is null, has a default value, an error, an empty assignmentKey,
     * or is not reportable, this method will no-op.
     *
     * @param featureAssignment The feature assignment.
     */
    notifyAssignmentListener(featureAssignment) {
        const skipAssignmentListener = !featureAssignment.assignmentKey ||
            !featureAssignment.reportable ||
            featureAssignment.errorValue ||
            featureAssignment.defaultValue !== undefined;
        if (skipAssignmentListener)
            return;
        try {
            this.assignmentListener?.(featureAssignment);
        }
        catch (error) {
            console.warn(error);
        }
    }
}
exports.FeaturesClient = FeaturesClient;
